import { ContentSourceEnum } from "@/types"

const embedUrlMap = {
  [ContentSourceEnum.youtube]: "https://www.youtube.com/embed/",
  [ContentSourceEnum.vimeo]: "https://player.vimeo.com/video/",
  [ContentSourceEnum.tiktok]: "https://www.tiktok.com/embed/video/",
}

/**
 * Convert a video URL to an embeddable iframe URL.
 * @param url - The canonical URL of the video.
 * @param source - The source platform of the video.
 * @returns The embeddable URL or null if conversion fails.
 */
export default function getEmbedUrl(
  url: string,
  source?: ContentSourceEnum | null,
): string | null {
  if (!url) return null

  try {
    // Ensure URL has protocol
    const urlWithProtocol =
      url.startsWith("http://") || url.startsWith("https://")
        ? url
        : `https://${url}`

    const urlObj = new URL(urlWithProtocol)
    const hostname = urlObj.hostname.toLowerCase()

    // Auto-detect platform URLs even if source isn't set
    const isYouTube =
      hostname.includes("youtube.com") || hostname.includes("youtu.be")
    const isVimeo = hostname.includes("vimeo.com")
    const isTikTok =
      hostname.includes("tiktok.com") || hostname.includes("vm.tiktok.com")

    // Handle YouTube URLs
    if (source === ContentSourceEnum.youtube || isYouTube) {
      // Handle YouTube URLs:
      // - youtube.com/watch?v=ID
      // - www.youtube.com/watch?v=ID
      // - youtu.be/ID
      // - youtube.com/embed/ID (already embed format)
      let youtubeId: string | null = null

      // Check if already in embed format
      if (urlObj.pathname.startsWith("/embed/")) {
        youtubeId = urlObj.pathname.split("/embed/")[1]?.split("?")[0]
      }
      // Check for watch?v= format
      else if (urlObj.searchParams.has("v")) {
        youtubeId = urlObj.searchParams.get("v")
      }
      // Check for youtu.be short URL
      else if (hostname.includes("youtu.be")) {
        youtubeId = urlObj.pathname.split("/").find(Boolean) ?? null
      }
      // Try to extract from pathname as fallback
      else {
        const pathParts = urlObj.pathname.split("/").filter(Boolean)
        youtubeId = pathParts[pathParts.length - 1] ?? null
      }

      return youtubeId
        ? `${embedUrlMap[ContentSourceEnum.youtube]}${youtubeId}`
        : null
    }

    // Handle Vimeo URLs
    if (source === ContentSourceEnum.vimeo || isVimeo) {
      const vimeoId = urlObj.pathname.split("/").filter(Boolean).pop()
      return vimeoId
        ? `${embedUrlMap[ContentSourceEnum.vimeo]}${vimeoId}`
        : null
    }

    // Handle TikTok URLs
    if (source === ContentSourceEnum.tiktok || isTikTok) {
      // TikTok URLs can be:
      // - tiktok.com/@username/video/VIDEO_ID
      // - vm.tiktok.com/VIDEO_ID (short URL)
      let tiktokId: string | null = null

      // Check for short URL format (vm.tiktok.com)
      if (hostname.includes("vm.tiktok.com")) {
        tiktokId = urlObj.pathname.split("/").find(Boolean) ?? null
      }
      // Check for standard format (tiktok.com/@username/video/VIDEO_ID)
      else if (urlObj.pathname.includes("/video/")) {
        const pathParts = urlObj.pathname.split("/video/")
        if (pathParts.length > 1) {
          tiktokId = pathParts[1].split("?")[0].split("/")[0]
        }
      }
      // Try to extract from pathname as fallback
      else {
        const pathParts = urlObj.pathname.split("/").filter(Boolean)
        // Look for a long numeric string (TikTok video IDs are typically long)
        tiktokId =
          pathParts.find((part) => /^\d+$/.test(part) && part.length > 10) ??
          pathParts[pathParts.length - 1] ??
          null
      }

      return tiktokId
        ? `${embedUrlMap[ContentSourceEnum.tiktok]}${tiktokId}`
        : null
    }

    // Handle other/unknown sources - try to embed the URL directly
    // Note: This may fail if the site has X-Frame-Options set to DENY or SAMEORIGIN
    if (
      source === ContentSourceEnum.other ||
      source === ContentSourceEnum.instagram ||
      !source
    ) {
      // Return the URL with proper protocol for iframe embedding
      // The browser will handle X-Frame-Options if the site doesn't allow embedding
      return urlWithProtocol
    }

    // For unknown sources, return null (can't embed)
    return null
  } catch {
    // If URL parsing fails, return null
    return null
  }
}
